import { convertPriceForUser } from "./currencyUtils";

/**
 * budgetUtils.js
 *
 * Funciones para calcular los totales de presupuesto del usuario.
 * Extraídas de App.js para mantenerlo limpio.
 *
 * Ambas funciones necesitan el mismo contexto:
 *   - userProfile: perfil del usuario (tiene committedGifts, groupBudgets, currency)
 *   - exchangeRates: tasas de cambio descargadas por useExchangeRates
 */

/**
 * Calcula los totales de presupuesto para UN grupo específico.
 *
 * Devuelve:
 *   budget    → límite que el usuario se puso para ese grupo
 *   committed → lo que tiene reservado pero aún no comprado
 *   spent     → lo que ya compró
 *   available → lo que le queda (budget - committed - spent)
 *   gifts     → lista de regalos reservados/comprados (para el carrito)
 *
 * Uso en App.js:
 *   const totals = getBudgetTotals(currentFamily?.id, userProfile, exchangeRates);
 */
export function getBudgetTotals(familyId, userProfile, exchangeRates) {
  // Filtramos solo los regalos comprometidos de este grupo
  const comprometidos = (userProfile?.committedGifts || []).filter(
    (g) => !g.familyId || g.familyId === familyId
  );

  let totalComprometido = 0;
  let totalGastado = 0;

  comprometidos.forEach((g) => {
    const result = convertPriceForUser(g.personalPrice ?? g.price, g.currency || "USD", userProfile, exchangeRates);
    if (!result) return;
    if (g.purchased) totalGastado += result.val;
    else totalComprometido += result.val;
  });

  // Convertimos el presupuesto guardado a la moneda actual del usuario
  const rawBudget = parseFloat((userProfile?.groupBudgets || {})[familyId] || 0);
  const currentCurrency = userProfile?.currency || "USD";
  const budgetFrom = (userProfile?.groupBudgetCurrencies || {})[familyId] || currentCurrency;

  let budget = rawBudget;
  if (budgetFrom !== currentCurrency && exchangeRates) {
    const rate = exchangeRates.rates[budgetFrom];
    if (rate) budget = rawBudget / rate;
  }

  return {
    budget,
    committed: totalComprometido,
    spent:     totalGastado,
    available: budget - totalComprometido - totalGastado,
    // Lista para el carrito de BudgetScreen
    gifts: comprometidos.map((g) => ({
      ...g,
      gift: {
        ...(g.gift || {}),
        id: g.giftId || g.id,
        name: g.gift?.name || g.name || g.giftName,
        photoUrl: g.gift?.photoUrl || g.photoUrl,
        price: g.personalPrice ?? g.price,
        currency: g.gift?.currency || g.currency,
      },
      memberName: g.memberName || g.ownerName || "",
      purchased: !!g.purchased,
      wishlistId: g.wishlistId,
    })),
  };
}

/**
 * Calcula los totales de presupuesto sumando TODOS los grupos del usuario.
 *
 * Útil para la vista general de presupuesto.
 *
 * Uso en App.js:
 *   const totalesGlobales = getBudgetTotalsAll(userProfile, exchangeRates);
 */
export function getBudgetTotalsAll(userProfile, exchangeRates) {
  const comprometidos = userProfile?.committedGifts || [];
  let totalComprometido = 0;
  let totalGastado = 0;

  comprometidos.forEach((g) => {
    const result = convertPriceForUser(g.personalPrice ?? g.price, g.currency || "USD", userProfile, exchangeRates);
    if (!result) return;
    if (g.purchased) totalGastado += result.val;
    else totalComprometido += result.val;
  });

  // Sumamos el presupuesto de todos los grupos convirtiendo cada uno
  const groupBudgets = userProfile?.groupBudgets || {};
  const groupBudgetCurrencies = userProfile?.groupBudgetCurrencies || {};
  const currentCurrency = userProfile?.currency || "USD";
  let totalBudget = 0;

  Object.keys(groupBudgets).forEach((fid) => {
    const rawB = parseFloat(groupBudgets[fid] || 0);
    if (!rawB) return;
    const budgetFrom = groupBudgetCurrencies[fid] || currentCurrency;
    if (budgetFrom !== currentCurrency && exchangeRates) {
      const rate = exchangeRates.rates[budgetFrom];
      totalBudget += rate ? rawB / rate : rawB;
    } else {
      totalBudget += rawB;
    }
  });

  return {
    budget:    totalBudget,
    committed: totalComprometido,
    spent:     totalGastado,
    available: totalBudget - totalComprometido - totalGastado,
  };
}
